import { PaletteMode, radioClasses } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { Color, ColorMono } from "kvweb-ui";
import CustomColor from "../utils/overrides/color";

declare module "@mui/material/styles" {
  interface Palette {
    neutral: Palette["primary"];
  }

  interface PaletteOptions {
    neutral?: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    neutral: true;
  }
}

const paletteTheme = {
  light: {
    primary: {
      main: "#0B6BFF",
      light: "#0090DA",
      dark: "#006FA8",
    },
    secondary: {
      main: "#19857b",
    },
    background: {
      default: "#FAFAFA",
    },
    text: {
      primary: "#23242A",
      secondary: "#323741;",
    },
    error: {
      main: "#DB4E65",
      dark: "#c62828",
    },
    neutral: {
      main: "#9C9C9C",
      dark: "#706868",
    },
  },
  dark: {
    primary: {
      main: "#00880C",
    },
    background: {
      default: "#111315",
    },
    text: {
      primary: "#FDFDFD",
      secondary: "#919AA4",
    },
    error: {
      main: "#DB4E65",
      dark: "#c62828",
    },
    neutral: {
      main: "#9C9C9C",
      dark: "#706868",
    },
  },
};

// Create a theme instance.
const theme = (mode: PaletteMode, config?: any) => {
  if (localStorage && localStorage.getItem("isWidgetMykiot")) {
    return createTheme({
      components: {}});
  }

  if (config.isKeivi && !config.isFromPageBuilder) {
    paletteTheme.light.primary.main = "#FF5C0A"
  }

  return createTheme({
    palette: {
      mode: "light",
      ...(mode === "light"
        ? {
            primary: {
              main: paletteTheme.light.primary.main,
              light: paletteTheme.light.primary.light,
              dark: paletteTheme.light.primary.dark,
            },
            secondary: {
              main: paletteTheme.light.secondary.main,
            },
            background: {
              default: paletteTheme.light.background.default,
            },
            text: {
              primary: paletteTheme.light.text.primary,
              secondary: paletteTheme.light.text.secondary,
            },
            error: {
              main: paletteTheme.light.error.main,
              dark: paletteTheme.light.error.dark,
            },
            neutral: {
              main: paletteTheme.light.neutral.main,
              dark: paletteTheme.light.neutral.dark,
            },
          }
        : {
            primary: {
              main: paletteTheme.dark.primary.main,
            },
            background: {
              default: paletteTheme.dark.background.default,
            },
            text: {
              primary: paletteTheme.dark.text.primary,
              secondary: paletteTheme.dark.text.secondary,
            },
            error: {
              main: paletteTheme.dark.error.main,
              dark: paletteTheme.dark.error.dark,
            },
            neutral: {
              main: paletteTheme.dark.neutral.main,
              dark: paletteTheme.dark.neutral.dark,
            },
          }),
    },
    components: !config.isFromPageBuilder ? {
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&:not([btntype="outlined"]):not([role="button"]):not([btntype="text"]):not(.MuiCheckbox-root):not([btntype="fade"]):not([aria-label]):not([aria-selected]):not([btncolor]):not([btntype="text"]):not([role="gridcell"]):not(.MuiSwitch-switchBase):not(.MuiButton-textSizeSmall):not([disabled]):not([data-mui-internal-clone-element]):not(.MuiRadio-root):not([data-btn-setting="button-banner-designer"])': {
              backgroundColor: `${paletteTheme.light.primary.main}!important`,
              "&:hover": {
                backgroundColor: `${CustomColor['primary-600']}!important`,
              }
            },
            '&.radio-button-popup': {
              '&:hover': {
                border: `1px solid ${paletteTheme.light.primary.main}!important`,
              },
              '&.button-active': {
                border: `1px solid ${paletteTheme.light.primary.main}!important`,
              },
              '&.button-active *': {
                color: `${paletteTheme.light.primary.main}!important`,
              },
              '&:before': {
                backgroundColor: `${paletteTheme.light.primary.main}!important`,
              }
            },
            '&:is([btntype="outlined"]):not(#usage-button):not([disabled])': {
              color: `${paletteTheme.light.primary.main}!important`,
              '&:hover': {
                backgroundColor: `${CustomColor['primary-25']}!important`,
              },
              '& svg path': {
                fill: `${paletteTheme.light.primary.main}!important`,
              }
            },
            '&:is([btntype="fade"]):not(#usage-button):not([disabled])': {
              outline: 'none',
              color: `${paletteTheme.light.primary.main}!important`,
              backgroundColor: `${CustomColor['primary-50']}!important`,
              '&:hover': {
                backgroundColor: `${CustomColor['primary-500']}!important`,
                color: `${ColorMono['color-white']}!important`,
              }
            },
            '&:is([btntype="fade"]):is(#usage-button):not([disabled])': {
              '&:hover': {
                backgroundColor: `${CustomColor['primary-25']}!important`,
              }
            },
            '&:is([btntype="text"]):not(#usage-button):not([disabled]):not(.MuiIconButton-root)': {
              outline: 'none',
              color: `${CustomColor['black-750']}!important`,
              '&:hover': {
                backgroundColor: `${CustomColor['primary-50']}!important`,
              },
              '& svg path': {
                fill: `${paletteTheme.light.primary.main}!important`,
              }
            },
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            "&.MuiInputBase-colorPrimary": {
              "&:hover>fieldset": {
                borderColor: `${paletteTheme.light.primary.main} !important`,
              }
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused[data-shrink=true]': {
              color: `${paletteTheme.light.primary.main} !important`,
            }
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E5E8EA",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: `${paletteTheme.light.primary.main} !important`,
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: `${paletteTheme.light.primary.main}`,
            '&:hover': {
              textDecorationColor: `${paletteTheme.light.primary.main}`,
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-switchBase.Mui-checked': {
              '& + .MuiSwitch-track': {
                backgroundColor: `${paletteTheme.light.primary.main}!important`,
              },
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected:not(.MuiPickersDay-dayWithMargin):not(.MuiMultiSectionDigitalClockSection-item)': {
              color: `${paletteTheme.light.primary.main}`,
              '& svg': {
                fill: `${paletteTheme.light.primary.main}`,
              },
            },
            '&:active': {
              color: `${paletteTheme.light.primary.main}`,
            }
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&.Mui-checked span.aaa': {
              backgroundColor: `${paletteTheme.light.primary.main}`,
              boxShadow: `inset 0 0 0 1px ${paletteTheme.light.primary.main},inset 0 -1px 0 ${paletteTheme.light.primary.main}`
            },
            '& input:disabled~span.aaa': {
              boxShadow: `inset 0 0 0 1px ${Color['black-100']}, inset 0 -1px 0 ${Color['black-100']}`,
              backgroundColor: `${Color['black-100']}`,
            },
            '& input:hover:not(disabled)~span:first-of-type:not(.MuiTouchRipple-root)': {
              boxShadow: `inset 0 0 0 1px ${paletteTheme.light.primary.main},inset 0 -1px 0 ${paletteTheme.light.primary.main}`
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: `${paletteTheme.light.primary.main} !important`
          },
          root: {
            '& .MuiButtonBase:is([role="tab"]) p': {
              color: `${paletteTheme.light.primary.main} !important`,
            }
          }
        }
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            '& .MuiTableRow-root.MuiTableRow-hover:hover': {
              backgroundColor: `${CustomColor['primary-25']} !important`
            }
          }
        }
      }
    } : {},
    spacing: 4,
    breakpoints: {
      values: {
        xs: 0,
        sm: 355,
        md: 748,
        lg: 1200,
        xl: 1550,
      },
    },
  });
};

export default theme;
